import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LuArrowLeft, LuPrinter, LuImageOff } from "react-icons/lu";
import { updateFloatPanel } from "../../../../../reducers/userConfigSlice";
import { orderCode } from "../list";
import {
  amount,
  orderDate,
  statusNames,
  paymentNames,
  orderEvents,
  addressText,
} from "./referenceModel";
const Card = ({ title, description, children, className = "" }) => (
  <section className={`orderDetailCard ${className}`}>
    <header>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
    </header>
    <div className="orderDetailCardBody">{children}</div>
  </section>
);
const Pair = ({ name, children, className = "" }) => (
  <div className={className}>
    <dt>{name}</dt>
    <dd>{children ?? "—"}</dd>
  </div>
);
export const OrderReference = ({
  data,
  actions,
  printTicket,
  createInvoice,
  printInvoice,
  refresh,
}) => {
  const dispatch = useDispatch();
  const backRef = useRef(null);
  useEffect(() => {
    backRef.current?.focus({ preventScroll: true });
  }, []);
  const lock = useRef(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const run = async (action) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      if (!(await refresh())) throw Error();
      setNotice("Solicitud de documento completada");
    } catch {
      setError(
        "No se pudo confirmar la acción del documento. Revisa su estado antes de repetirla.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const products = Array.isArray(data.products) ? data.products : [];
  const quantities = products.map((p) => p.quantity);
  const units = quantities.every(
    (q) => q != null && String(q).trim() !== "" && Number.isFinite(Number(q)),
  )
    ? quantities.reduce((sum, q) => sum + Number(q), 0)
    : null;
  const events = orderEvents(data.OrderLog);
  const customer = data.Customer || {};
  const money = data.amounts || {};
  const payment =
    paymentNames[data.paymentStatus] || data.paymentStatus || "Sin información";
  const status = statusNames[data.status] || data.status || "Sin información";
  const invoice = data.Invoice;
  const notes = Array.isArray(data.notes)
    ? data.notes.filter((n) => typeof n === "string").join("\n")
    : typeof data.notes === "string"
      ? data.notes
      : "";
  const email = data.email || customer.email,
    phone = data.phone || customer.phone;
  return (
    <div className="orderReference" aria-busy={busy}>
      <button
        type="button"
        className="orderBack"
        ref={backRef}
        disabled={busy}
        onClick={() =>
          dispatch(
            updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
          )
        }
      >
        <LuArrowLeft aria-hidden="true" />
        Volver a pedidos
      </button>
      <header className="orderDetailHeading">
        <div>
          <h2 id="admin-editor-title">Detalles del pedido</h2>
          <div className="orderDetailBadges">
            <span className={`ordersBadge state-${data.paymentStatus}`}>
              {payment}
            </span>
            <span className={`ordersBadge state-${data.status}`}>{status}</span>
          </div>
        </div>
        <button
          type="button"
          className="orderPrint"
          disabled={busy}
          onClick={() => run(printTicket)}
        >
          <LuPrinter aria-hidden="true" />
          {busy ? "Procesando…" : "Imprimir ticket"}
        </button>
      </header>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <div className="orderDetailColumns">
        <div className="orderDetailMain">
          <Card title="Pedido">
            <label className="orderStatusField">
              Estado del pedido
              <select
                aria-label="Estado del pedido"
                value={data.status || ""}
                disabled
              >
                <option value={data.status || ""}>{status}</option>
              </select>
            </label>
            <dl className="orderFactsGrid">
              <Pair name="Nº de pedido">{orderCode(data)}</Pair>
              <Pair name="Tiempo de preparación">
                {data.preparationTime == null
                  ? "—"
                  : `${data.preparationTime} min`}
              </Pair>
              <Pair name="Fecha de pedido">{orderDate(data.createdAt)}</Pair>
            </dl>
          </Card>
          <Card
            title="Productos"
            description={
              units == null
                ? `${products.length} líneas`
                : `${units} ${units === 1 ? "unidad" : "unidades"}`
            }
            className="orderDetailProducts"
          >
            {products.map((p, i) => (
              <article key={p.orderProductId || p.id || i}>
                <span className="orderProductImageFallback">
                  <LuImageOff aria-hidden="true" />
                  {typeof p.images?.[0] === "string" && p.images[0] && (
                    <img
                      src={p.images[0]}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.hidden = true;
                      }}
                    />
                  )}
                </span>
                <div className="orderLineDescription">
                  <div>
                    <strong>{p.name || "Producto sin nombre"}</strong>
                    <strong>{amount(p.lineTotalWithTax)}</strong>
                  </div>
                  <p>
                    {p.quantity ?? "—"} × {amount(p.unitPriceWithTax)}
                  </p>
                  {p.variantName && <p>{p.variantName}</p>}
                  {Array.isArray(p.items) &&
                    p.items.some((item) => item?.name) && (
                      <p>
                        {p.items
                          .map((item) => item?.name)
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  {p.note && <p className="orderLineNote">{p.note}</p>}
                </div>
              </article>
            ))}
            {!products.length && <p>Sin productos disponibles</p>}
          </Card>
          {actions && <div className="orderExistingActions">{actions}</div>}
        </div>
        <div className="orderDetailSecondary">
          <Card title="Resumen económico">
            <dl className="orderEconomic">
              <Pair name="Nº de factura">
                {invoice?.InvoiceNumber || invoice?.number || "—"}
              </Pair>
              <Pair name="Canal de venta">
                {data.SalesChannel?.name || "—"}
              </Pair>
              <Pair name="Subtotal sin IVA">{amount(money.subtotal)}</Pair>
              <Pair name="Descuento sin IVA">{amount(money.discounts)}</Pair>
              <Pair name="Base imponible">{amount(money.base)}</Pair>
              <Pair name="Envío">{amount(money.shipping)}</Pair>
              <Pair name="Impuestos">{amount(money.tax)}</Pair>
              <Pair name="Total" className="orderTotalRow">
                <strong className="orderGrandTotal">
                  {amount(money.total)}
                </strong>
              </Pair>
              <Pair name="Estado del pago">
                <span className={`ordersBadge state-${data.paymentStatus}`}>
                  {payment}
                </span>
              </Pair>
              <Pair name="Importe pagado">—</Pair>
            </dl>
            {!data.amounts && (
              <p role="alert">
                El importe de este pedido necesita revisión antes del cobro.
              </p>
            )}
            <details className="orderDocuments">
              <summary>Documentos del pedido</summary>
              <button
                type="button"
                disabled={busy || !data.amounts}
                onClick={() => run(invoice ? printInvoice : createInvoice)}
              >
                {invoice ? "Imprimir factura" : "Crear factura"}
              </button>
            </details>
          </Card>
          <Card title="Notas">
            <p className="orderPreserveLines">
              {notes || "Sin notas del pedido"}
            </p>
          </Card>
          <Card title="Cliente">
            <dl className="orderEconomic">
              <Pair name="Nombre">
                {customer.fullname || customer.fullName || "Cliente contado"}
              </Pair>
              <Pair name="Nº de pedidos">—</Pair>
              <Pair name="Email">
                {email ? <a href={`mailto:${email}`}>{email}</a> : "—"}
              </Pair>
              <Pair name="Teléfono">
                {phone ? (
                  <a href={`tel:${String(phone).replace(/\s+/g, "")}`}>
                    {phone}
                  </a>
                ) : (
                  "—"
                )}
              </Pair>
            </dl>
          </Card>
          <Card title="Dirección de envío">
            <p>{addressText(data.address)}</p>
          </Card>
          <Card title="Dirección de facturación">
            <p>Sin dirección de facturación disponible</p>
          </Card>
        </div>
        <aside className="orderDetailTimeline">
          <Card title="Cronología del pedido">
            {events.length ? (
              <ol>
                {events.map((event, i) => (
                  <li
                    key={event.key}
                    className={
                      i === events.length - 1 ? "orderEventLatest" : ""
                    }
                  >
                    <strong>{event.name}</strong>
                    <time dateTime={event.date}>{orderDate(event.date)}</time>
                  </li>
                ))}
              </ol>
            ) : (
              <p>Sin eventos disponibles</p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
};
